import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { BrandIcon } from './BrandIcon';

interface FooterProps {
  variant?: 'default' | 'room';
}

export function Footer({ variant = 'default' }: FooterProps) {
  const { t } = useTranslation();
  const isRoom = variant === 'room';

  const linkClass = isRoom
    ? 'text-room-text-dim transition-colors hover:text-room-accent text-sm'
    : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 text-sm transition-colors';

  const compactLinkClass = isRoom
    ? 'text-room-text-faint transition-colors hover:text-room-accent text-xs'
    : 'text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 text-xs transition-colors';

  const legalLinks = (
    <>
      <Link to="/privacy" className={compactLinkClass}>
        {t('footer.privacyPolicy')}
      </Link>
      <span className={isRoom ? 'text-room-border-strong' : 'text-gray-300 dark:text-gray-600'}>
        ·
      </span>
      <Link to="/terms" className={compactLinkClass}>
        {t('footer.termsOfService')}
      </Link>
      <span className={isRoom ? 'text-room-border-strong' : 'text-gray-300 dark:text-gray-600'}>
        ·
      </span>
      <Link to="/cookies" className={compactLinkClass}>
        {t('footer.cookiePolicy')}
      </Link>
    </>
  );

  const compactFooter = (
    <footer
      className={
        isRoom
          ? 'mt-4 border-t border-room-border px-1 pb-1 pt-4 text-center'
          : 'mt-auto border-t border-gray-200 bg-white px-4 py-4 dark:border-gray-700 dark:bg-gray-800'
      }
    >
      <div className="mb-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
        {legalLinks}
      </div>
      <a href="mailto:info@calendin.app" className={`${compactLinkClass} mb-1.5 inline-block`}>
        info@calendin.app
      </a>
      <p className={`text-[10px] leading-snug ${isRoom ? 'text-room-text-faint' : 'text-gray-500 dark:text-gray-400'}`}>
        {t('footer.copyright')}
      </p>
    </footer>
  );

  const desktopFooter = (
    <footer
      className={
        isRoom
          ? 'mt-8 border-t border-room-border pt-8'
          : 'mt-auto border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
      }
    >
      <div className={`mx-auto max-w-4xl px-4 ${isRoom ? 'pb-6' : 'py-4'}`}>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <BrandIcon size={32} />
              <span
                className={`text-xl font-bold ${isRoom ? 'text-room-text' : 'text-gray-900 dark:text-white'}`}
              >
                CalendIn™
              </span>
            </div>
            <p className={`text-sm ${isRoom ? 'text-room-text-dim' : 'text-gray-600 dark:text-gray-400'}`}>
              {t('footer.description')}
            </p>
          </div>

          <div className="space-y-4">
            <h3
              className={`text-sm font-semibold uppercase tracking-wider ${isRoom ? 'text-room-text' : 'text-gray-900 dark:text-white'}`}
            >
              {t('footer.legal')}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy" className={linkClass}>
                  {t('footer.privacyPolicy')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className={linkClass}>
                  {t('footer.termsOfService')}
                </Link>
              </li>
              <li>
                <Link to="/cookies" className={linkClass}>
                  {t('footer.cookiePolicy')}
                </Link>
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3
              className={`text-sm font-semibold uppercase tracking-wider ${isRoom ? 'text-room-text' : 'text-gray-900 dark:text-white'}`}
            >
              {t('footer.contact')}
            </h3>
            <div className="flex items-center space-x-2">
              <Mail className={`h-4 w-4 ${isRoom ? 'text-room-text-faint' : 'text-gray-500 dark:text-gray-400'}`} />
              <a href="mailto:info@calendin.app" className={linkClass}>
                info@calendin.app
              </a>
            </div>
          </div>
        </div>

        <div
          className={`mt-8 border-t pt-6 ${isRoom ? 'border-room-border' : 'border-gray-200 dark:border-gray-700'}`}
        >
          <div className="flex flex-col items-center justify-between space-y-4 md:flex-row md:space-y-0">
            <p className={`text-sm ${isRoom ? 'text-room-text-faint' : 'text-gray-500 dark:text-gray-400'}`}>
              {t('footer.copyright')}
            </p>
            <p className={`text-sm ${isRoom ? 'text-room-text-faint' : 'text-gray-500 dark:text-gray-400'}`}>
              {t('footer.madeWith')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );

  return (
    <>
      <div className="lg:hidden">{compactFooter}</div>
      <div className="hidden lg:block">{desktopFooter}</div>
    </>
  );
}
